import React from 'react'
import clsx from 'clsx'
import { useTheme, useMediaQuery } from '@mui/material'
import { useAppSelector, useAppDispatch } from '../../../hooks/redux'

import Input from '../../../components/molecules/Search'
import Spacer from '../../../components/atoms/Spacer'
import Button2 from '../../../components/atoms/Button2'
import LoadingSpinner from '../../../components/atoms/LoadingSpinner'
import ArticleTable from '../../../components/molecules/ArticleTable'
import ArticleCards from '../ArticleCards'
import NewDocumentFolderModal from '../../../components/molecules/ChatModals/NewDocumentFolderModal'
import useAccess, { PERMS } from '@SonarCX/hooks/useAccess'

import {
  selectArticles,
  setCreating,
  setSearchVal
} from '../../../slices/article'
import useStyles from './styles'

const DocumentTableView = () => {
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isTableSquished = useMediaQuery(theme.breakpoints.down(815))
  const hasPermission = useAccess()

  const { root, selected, articles, searchVal, searched, searching } =
    useAppSelector(selectArticles)

  const [needsPadding, setNeedsPadding] = React.useState(false)
  const [folderModalOpen, setFolderModalOpen] = React.useState(false)
  const hasPermissions = React.useMemo(
    () => hasPermission([PERMS.ArticlesFull, PERMS.ArticlesAdmin]),
    [hasPermission]
  )

  const title = React.useMemo(() => {
    const loadText = 'Loading...'

    if (!root) {
      return loadText
    }

    if (searched) {
      return `Search Results (${searched.total_items})`
    }

    if (!selected) {
      const count = root.items.reduce(
        (prev, curr) => prev + curr.total_items,
        0
      )
      return `All Articles (${count})`
    }

    if (!(selected in articles)) {
      return loadText
    }

    const article = articles[selected]
    return `${article.title} (${article.total_items})`
  }, [root, selected, articles, searched])

  const selectedItem = React.useMemo(() => {
    const loadText = 'Loading...'
    if (!root) {
      return loadText
    }

    if (searched) {
      return searched.items
    }

    if (!selected) {
      return [...root.items]
    }

    const item = articles[selected]
    if (item && item.type === 'folder') {
      return item.items
    }
    return item
  }, [root, searched, articles, selected])

  React.useLayoutEffect(() => {
    const tableContainer = document.querySelector('#article-table-container')
    const tableDoesntHaveVerticalScrollbar =
      tableContainer?.scrollHeight > tableContainer?.clientHeight

    setNeedsPadding(tableDoesntHaveVerticalScrollbar)
  }, [selected])

  const classes = useStyles({ extraPadding: needsPadding })

  const handleDocumentCreateClick = React.useCallback(() => {
    dispatch(setCreating(true))
  }, [dispatch])

  return (
    <>
      <div className={classes.root}>
        <div className={classes.header}>
          <div className={classes.title}>{title}</div>

          <Input
            placeholder='Search document ...'
            value={searchVal}
            onChange={e => dispatch(setSearchVal(e.target.value))}
            maxLength={20}
            className={classes.search}
          />

          <Spacer />

          {hasPermissions && (
            <div className={classes.buttons}>
              {selected === null && (
                <Button2
                  onClick={() => setFolderModalOpen(true)}
                  className={clsx(classes.button, classes.folderButton)}
                >
                  + New Folder
                </Button2>
              )}

              <Button2
                onClick={handleDocumentCreateClick}
                className={classes.button}
              >
                + New Article
              </Button2>
            </div>
          )}
        </div>

        {searching ? (
          <LoadingSpinner />
        ) : isTableSquished ? (
          <ArticleCards items={selectedItem} />
        ) : (
          <ArticleTable items={selectedItem} />
        )}
      </div>

      {folderModalOpen && (
        <NewDocumentFolderModal onClose={() => setFolderModalOpen(false)} />
      )}
    </>
  )
}

export default DocumentTableView
